import React, { useState, useMemo } from 'react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import PropertyFilter from '@amzn/awsui-components-react/polaris/property-filter';
import {
  Query,
  Token,
  FilteringProperty,
} from '@amzn/awsui-components-react/polaris/property-filter/interfaces';
import { Tag } from 'src/components/common/ui/Tags/Tags';
import CompletedReviewsTableFilterTag from './CompletedReviewsTableFilterTag';
import { CompletedReviewsTableColumn } from './CompletedReviewsColumnConfig';
import './CompletedReviewsTableFilter.scss';

const LIKE_OPERATOR = ':';
const EMPTY_QUERY: Query = { operation: 'and', tokens: [] };
const DEFAULT_FILTER_PROPERTY =
  CompletedReviewsTableColumn.MESSAGE_GROUP.property;

type Props = {
  onFilterChange: (filters: Token[]) => void;
};

/**
 * Completed Reviews Table Filter
 *
 * Manages filter state and displays applied filters as tags for completed reviews table.
 */
const CompletedReviewsTableFilter: React.FC<Props> = ({ onFilterChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [appliedFilters, setAppliedFilters] = useState<Token[]>([]);

  // Build filterable properties excluding timestamp fields
  const completedReviewsSearchProperties: FilteringProperty[] = useMemo(
    () =>
      Object.entries(CompletedReviewsTableColumn)
        .filter(
          ([, column]) =>
            column.property !==
              CompletedReviewsTableColumn.REQUESTED_TIMESTAMP.property &&
            column.property !==
              CompletedReviewsTableColumn.REVIEWED_TIMESTAMP.property
        )
        .map(([, column]) => ({
          key: column.property,
          propertyLabel: t(column.translationKey),
          groupValuesLabel: t(column.translationKey),
          defaultOperator: LIKE_OPERATOR,
          operators: [LIKE_OPERATOR],
        })),
    [t]
  );

  // Handle addition of a new filter
  const onQueryChange = (newQuery: Query) => {
    // Clear the input field after filter is applied
    setQuery(EMPTY_QUERY);
    // Apply default filter property and operator if missing
    const newFilterTokens = newQuery.tokens.map((token) => ({
      ...token,
      operator: token.operator || LIKE_OPERATOR,
      propertyKey: token.propertyKey || DEFAULT_FILTER_PROPERTY,
    }));
    // Accumulate applied filters with deduplication
    const filterMap = new Map(
      [...appliedFilters, ...newFilterTokens].map((filter) => [
        `${filter.propertyKey}:${filter.value}`,
        filter,
      ])
    );
    const newFilters = Array.from(filterMap.values());
    setAppliedFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Handle removal of filter tag
  const onTagRemove = (e: React.MouseEvent, tag: Tag) => {
    const updatedFilters = appliedFilters.filter(
      (filter) => !(filter.propertyKey === tag.id && filter.value === tag.name)
    );
    setAppliedFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  return (
    <div className="completed-reviews-filter-container">
      <div className="completed-reviews-filter-row">
        {/* Search input for filtering reviews */}
        <PropertyFilter
          className="completed-reviews-property-filter"
          filteringPlaceholder={t('completed-reviews-filter-placeholder')}
          filteringProperties={completedReviewsSearchProperties}
          filteringErrorText={t('completed-reviews-filter-error-text')}
          onChange={({ detail }) => onQueryChange(detail)}
          query={query}
        />
        {/* Display applied filters as removable tags */}
        {appliedFilters.length > 0 && (
          <div className="completed-reviews-applied-filters-section">
            <span className="completed-reviews-applied-filter-label">
              {t('completed-reviews-page-applied-filters')}
            </span>
            <CompletedReviewsTableFilterTag
              id="completed-reviews-filter-tags"
              appliedFilters={appliedFilters}
              onTagRemove={onTagRemove}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CompletedReviewsTableFilter;
